import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { pagesData, servicesData } from '../data';
import { mapCategoryToService, allowedFilenames } from './GalleryPage';
import SEO from '../components/SEO';
import ContactBox from '../components/ContactBox';
import Breadcrumbs from '../components/Breadcrumbs';
import { createBreadcrumbSchema, createServiceSchema } from '../lib/schema';
import { renderFormattedText } from '../lib/utils';
import { Phone, Mail, ChevronRight, ChevronLeft, ArrowLeft, CheckCircle2, ChevronDown } from 'lucide-react';
import * as Icons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const renderTextWithBold = (text, options = {}) => {
  return renderFormattedText(text, options);
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeService = searchParams.get('service');
  const page = pagesData[slug];
  const effectiveActiveService = slug === 'kloakmester-arbejde' ? (activeService || 'separering') : activeService;
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [openAccordionIdx, setOpenAccordionIdx] = useState(0);
  const [openFaqIdx, setOpenFaqIdx] = useState(0);

  // Redirect old tree felling page to the new integrated section
  useEffect(() => {
    if (slug === 'traefaeldning-og-haveservice') {
      navigate('/haveservice#traefaeldning', { replace: true });
    }
    if (slug === 'kloak-separering') {
      navigate('/kloakseparering', { replace: true });
    }
    if (slug === 'kloakmesterarbejde') {
      navigate('/kloakmester-arbejde', { replace: true });
    }
  }, [slug, navigate]);

  // Handle scrolling to hash element
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [slug, window.location.hash]);

  // Reset page when slug changes
  useEffect(() => {
    setCurrentPage(1);
    setOpenAccordionIdx(0);
    setOpenFaqIdx(0);
  }, [slug]);

  const subServices = {
    'separering': {
      title: 'Kloakseparering',
      description: 'Har du fået et påbud om at adskille regnvand og spildevand, hjælper vi dig sikkert gennem hele opgaven. Entreprenør Jan Hedegaard udfører kloakseparering i Galten og omegn for både private og erhverv.',
      points: [
        'Overholdelse af kommunale påbud',
        'Sikring mod kælderoversvømmelser',
        'Miljøvenlig håndtering af regnvand',
        'Professionel rådgivning og udførelse'
      ]
    },
    'spildevand': {
      title: 'Spildevand',
      description: 'Professionel håndtering og forbedret rensning af spildevand, nedsivningsanlæg samt tilslutning til det offentlige kloaknet. Vi sikrer, at spildevandet fra din bolig eller virksomhed ledes sikkert og miljømæssigt forsvarligt væk i overensstemmelse med alle gældende regler.',
      points: [
        'Etablering af nedsivningsanlæg',
        'Tilslutning til offentlig kloak',
        'Fejlfinding og reparation af spildevandsrør',
        'Autoriseret kvalitetssikring'
      ]
    },
    'tv-inspektion': {
      title: 'TV Inspektion',
      description: 'Vi udfører TV-inspektion af kloakrør for at lokalisere brud, rødder, sætningsskader eller forstoppelser hurtigt og præcist uden unødig opgravning. Med vores avancerede kameraudstyr kan vi se præcis, hvad der gemmer sig i dine rør, og give dig den mest effektive løsning.',
      points: [
        'Hurtig og præcis fejlfinding',
        'Ingen unødig opgravning',
        'Dokumentation af kloakkens tilstand',
        'Lokalisering af rotteangreb og rødder'
      ]
    }
  };

  if (!page) {
    return (
      <div className="py-40 text-center">
        <h1 className="text-4xl font-bold text-stone-900 mb-4">Siden blev ikke fundet</h1>
        <p className="text-stone-600 mb-8">Den side, du leder efter, findes desværre ikke.</p>
        <Link to="/" className="text-orange-600 font-bold hover:underline">Gå til forsiden</Link>
      </div>
    );
  }

  // Find other services for the sidebar
  const otherServices = servicesData.filter(s => s.slug !== slug).slice(0, 4);

  // Determine the hero image
  const heroImage = page.images?.[0]?.src || page.meta?.ogImage || "https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/7544e4c2-59ce-4207-b379-e1dab6e0007b.jpg";

  // Check if it's the 404 page (anlaeg-og-brolaegning)
  if (slug === 'anlaeg-og-brolaegning') {
    return (
      <>
        <SEO title={page.title} description="" />
        <section className="py-40 bg-stone-50 flex items-center justify-center min-h-[70vh]">
          <div className="max-w-md mx-auto px-4 text-center">
            <h1 className="text-9xl font-extrabold text-orange-600 mb-4">{page.headline}</h1>
            <h2 className="text-3xl font-bold text-stone-900 mb-4">{page.subhead}</h2>
            <p className="text-stone-600 mb-8 leading-relaxed">{page.text}</p>
            <Link 
              to={page.buttonHref}
              className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-md text-lg font-bold hover:bg-orange-700 transition-all shadow-xl shadow-orange-900/40"
            >
              {page.buttonText}
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </>
    );
  }

  const currentServiceInfo = servicesData.find(s => s.slug === slug);
  const breadcrumbItems = [
    { label: currentServiceInfo ? currentServiceInfo.title : (page.headline || 'Ydelse'), href: `/${slug}` }
  ];

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Forside', url: 'https://janhedegaard.dk/' },
    { name: currentServiceInfo ? currentServiceInfo.title : page.headline, url: `https://janhedegaard.dk/${slug}` }
  ]);

  const serviceSchema = createServiceSchema({
    name: currentServiceInfo ? currentServiceInfo.title : page.headline,
    description: page.meta?.description || page.subhead,
    url: `https://janhedegaard.dk/${slug}`,
    image: heroImage,
    serviceType: currentServiceInfo?.title || 'Kloakarbejde & Entreprenørarbejde'
  });

  const schemaList = [
    breadcrumbSchema,
    serviceSchema,
    ...(page.schema_markup ? [page.schema_markup] : [])
  ];

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta?.description || page.meta?.ogDescription || page.subhead}
        image={heroImage}
        schemaMarkup={schemaList}
      />

      {/* Hero */}
      <section className="relative min-h-[480px] py-24 md:py-28 flex items-center bg-stone-900">
        <div className="absolute inset-0">
          <img 
            src={heroImage.startsWith('https://assets.auxodev.dk/') && !heroImage.includes('/cdn-cgi/image/')
              ? heroImage.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/')
              : heroImage} 
            alt={page.headline} 
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-900/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-16 pt-[40px]">
          <div className="mb-6">
            <Breadcrumbs items={breadcrumbItems} isDark={true} />
          </div>
          <h1 className="text-[1.8rem] md:text-6xl font-bold text-white drop-shadow-lg max-w-4xl leading-[1.15]">
            {page.headline}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-stone-300 max-w-2xl drop-shadow leading-relaxed">
            {renderFormattedText(page.subhead, { isDark: true })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Left Column: Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Contact details bar */}
              {page.phone && page.email && (
                <div className="flex flex-wrap gap-6 text-sm text-stone-500 border-b border-stone-100 pb-6">
                  <a href={`tel:${page.phone.replace(/\s+/g, '')}`} className="hover:text-orange-600 transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4 text-orange-600" />
                    {page.phone}
                  </a>
                  <a href={`mailto:${page.email}`} className="hover:text-orange-600 transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4 text-orange-600" />
                    {page.email}
                  </a>
                </div>
              )}

              {/* Render specific layouts based on slug */}
              {slug === 'haveservice' && (
                <div className="space-y-12">
                  <div className="space-y-8">
                    <h2 className="text-3xl font-bold text-stone-900">{page.introTitle}</h2>
                    <p className="text-lg text-stone-600 leading-relaxed">{page.introText}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {page.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 bg-stone-50 p-4 rounded-lg border border-stone-100">
                          <CheckCircle2 className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                          <span className="text-stone-700 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-lg text-stone-600 leading-relaxed italic font-medium bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl">
                      {page.outroText}
                    </p>
                  </div>

                  {/* Integrated Tree Felling Section */}
                  {page.treeFelling && (
                    <div id="traefaeldning" className="pt-12 border-t border-stone-100 space-y-8 scroll-mt-24">
                      <div className="space-y-4">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                          <i className="fa-solid fa-tree text-[10px]"></i> Specialydelse
                        </span>
                        <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                          {page.treeFelling.title}
                        </h2>
                        <p className="text-xl text-stone-500 font-medium leading-relaxed">
                          {page.treeFelling.subtitle}
                        </p>
                      </div>

                      <div className="space-y-6">
                        {page.treeFelling.paragraphs.map((p, i) => (
                          <p key={i} className="text-lg text-stone-600 leading-relaxed">
                            {renderTextWithBold(p)}
                          </p>
                        ))}
                      </div>

                      {/* Features Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                        {page.treeFelling.features.map((feature, i) => (
                          <div key={i} className="bg-stone-50 border border-stone-100 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4">
                              <i className={`fa-solid ${
                                i === 0 ? 'fa-shield-halved' :
                                i === 1 ? 'fa-award' :
                                i === 2 ? 'fa-truck-ramp-box' : 'fa-scissors'
                              } text-lg`}></i>
                            </div>
                            <h3 className="text-lg font-bold text-stone-900 mb-2">{feature.title}</h3>
                            <p className="text-stone-600 text-sm leading-relaxed">{feature.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {slug === 'anlaeg-og-belaegning' && (
                <div className="space-y-12">
                  {/* Intro Section */}
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-stone-900 tracking-tight">{page.introTitle}</h2>
                    {page.paragraphs.map((p, i) => (
                      <p key={i} className="text-lg text-stone-600 leading-relaxed">{renderTextWithBold(p)}</p>
                    ))}
                  </div>

                  {/* Core Specialities Grid */}
                  <div className="pt-6 space-y-8">
                    <div className="border-l-4 border-orange-600 pl-4">
                      <h3 className="text-2xl font-bold text-stone-900">Vores specialer inden for anlæg & belægning</h3>
                      <p className="text-stone-500 mt-1">Vi leverer holdbare løsninger i højeste håndværksmæssige kvalitet.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-stone-50 border border-stone-100 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-4">
                          <i className="fa-solid fa-layer-group text-xl"></i>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Flisebelægning & Terrasser</h3>
                        <p className="text-stone-600 text-sm leading-relaxed">
                          Nyetablering af terrasser, gangstier og indkørsler med fliser eller herregårdssten. Vi sikrer et stabilt underlag, der modstår det danske vejr og ikke skrider med tiden.
                        </p>
                      </div>

                      <div className="bg-stone-50 border border-stone-100 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-4">
                          <i className="fa-solid fa-cubes text-xl"></i>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Klassisk Brolægning</h3>
                        <p className="text-stone-600 text-sm leading-relaxed">
                          Traditionel brolægning med brosten, chaussésten og kantsten. Perfekt til herregårde, historiske ejendomme eller dig, der ønsker et klassisk, elegant og ekstremt slidstærkt udtryk.
                        </p>
                      </div>

                      <div className="bg-stone-50 border border-stone-100 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-4">
                          <i className="fa-solid fa-border-all text-xl"></i>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Støttemure & Trapper</h3>
                        <p className="text-stone-600 text-sm leading-relaxed">
                          Opbygning af solide støttemure til niveauforskelle i haven samt flotte trapper, der integreres naturligt i terrænet. Vi sikrer korrekt dræning og jordsikring.
                        </p>
                      </div>

                      <div className="bg-stone-50 border border-stone-100 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-4">
                          <i className="fa-solid fa-trowel-bricks text-xl"></i>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 mb-2">In-situ støbt beton</h3>
                        <p className="text-stone-600 text-sm leading-relaxed">
                          Moderne, råt og eksklusivt udtryk med in-situ støbte betonterasser og stier. En fugefri og minimalistisk løsning, der tilpasses præcis efter din haves geometri.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <ContactBox 
                      title={`Få et tilbud på ${page.headline.toLowerCase()}`}
                      subtitle="Vi står klar til at hjælpe dig med din næste opgave. Kontakt os i dag for et uforpligtende tilbud."
                      compact={true}
                    />
                  </div>
                </div>
              )}

              {slug === 'stoebning-af-beton' && (
                <div className="space-y-8">
                  <h2 className="text-3xl font-bold text-stone-900">{page.introTitle}</h2>
                  <p className="text-lg text-stone-600 leading-relaxed">{page.introText}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {page.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 bg-stone-50 p-4 rounded-lg border border-stone-100">
                        <CheckCircle2 className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                        <span className="text-stone-700 font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                  {page.paragraphs.map((p, i) => (
                    <p key={i} className="text-lg text-stone-600 leading-relaxed">{renderTextWithBold(p)}</p>
                  ))}
                  <div className="pt-6">
                    <ContactBox 
                      title={`Få et tilbud på ${page.headline.toLowerCase()}`}
                      subtitle="Vi står klar til at hjælpe dig med din næste opgave. Kontakt os i dag for et uforpligtende tilbud."
                      compact={true}
                    />
                  </div>
                </div>
              )}

              {slug === 'traefaeldning-og-haveservice' && (
                <div className="space-y-8">
                  <h2 className="text-3xl font-bold text-stone-900">{page.introTitle}</h2>
                  {page.paragraphs.map((p, i) => (
                    <p key={i} className="text-lg text-stone-600 leading-relaxed">{renderTextWithBold(p)}</p>
                  ))}
                  <div className="pt-6">
                    <ContactBox 
                      title={`Få et tilbud på ${page.headline.toLowerCase()}`}
                      subtitle="Vi står klar til at hjælpe dig med din næste opgave. Kontakt os i dag for et uforpligtende tilbud."
                      compact={true}
                    />
                  </div>
                </div>
              )}

              {['ny-indkoersel', 'kloak-separering', 'kloakseparering', 'entreprenoerarbejde', 'kloakservice', 'tv-inspektion', 'inspektion'].includes(slug) && (
                <div className="space-y-12">
                  {page.sections.map((section, idx) => (
                    <div key={idx} className="space-y-4">
                      <h2 className="text-2xl font-bold text-stone-900">{section.title}</h2>
                      {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                        <p key={pIdx} className="text-lg text-stone-600 leading-relaxed">
                          {renderTextWithBold(p)}
                        </p>
                      ))}
                      {section.list && (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 bg-stone-50 p-4 rounded-lg border border-stone-100">
                              <CheckCircle2 className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                              <span className="text-stone-700 font-medium">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.subsections && section.subsections.map((sub, sIdx) => (
                        <div key={sIdx} className="pt-4 space-y-3">
                          <h3 className="text-xl font-bold text-stone-900">{sub.title}</h3>
                          {sub.paragraphs && sub.paragraphs.map((sp, spIdx) => (
                            <p key={spIdx} className="text-lg text-stone-600 leading-relaxed">
                              {renderTextWithBold(sp)}
                            </p>
                          ))}
                        </div>
                      ))}
                      {section.outro && (
                        <p className="text-lg text-stone-600 leading-relaxed italic font-medium pt-4">
                          {section.outro}
                        </p>
                      )}
                    </div>
                  ))}

                  {/* FAQ section */}
                  {page.faq && page.faq.display_on_page && page.faq.items && page.faq.items.length > 0 && (
                    <div className="pt-10 border-t border-stone-200 space-y-6">
                      <div className="space-y-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                          <i className="fa-solid fa-circle-question text-[11px]" aria-hidden="true" /> Spørgsmål & Svar
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
                          {page.faq.section_heading?.text || 'Ofte stillede spørgsmål'}
                        </h2>
                      </div>

                      <div className="space-y-4 pt-2">
                        {page.faq.items.map((faqItem, fIdx) => {
                          const isOpen = openFaqIdx === fIdx;
                          return (
                            <div 
                              key={fIdx} 
                              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                                isOpen 
                                  ? 'border-orange-200 bg-orange-50/30 shadow-sm' 
                                  : 'border-stone-200 bg-white hover:border-stone-300'
                              }`}
                            >
                              <h3 className="m-0 p-0 text-inherit font-inherit">
                                <button
                                  type="button"
                                  onClick={() => setOpenFaqIdx(isOpen ? null : fIdx)}
                                  className="w-full flex items-center justify-between p-5 text-left font-bold text-lg text-stone-900 hover:text-orange-600 transition-colors focus:outline-none cursor-pointer"
                                  aria-expanded={isOpen}
                                >
                                  <span className="pr-4">{faqItem.question}</span>
                                  <ChevronDown 
                                    className={`w-5 h-5 text-stone-500 transition-transform duration-300 shrink-0 ${
                                      isOpen ? 'transform rotate-180 text-orange-600' : ''
                                    }`} 
                                  />
                                </button>
                              </h3>

                              <AnimatePresence initial={false}>
                                {isOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: "easeInOut" }}
                                  >
                                    <div className="px-5 pb-5 text-stone-600 leading-relaxed border-t border-stone-100 pt-3">
                                      <p>{faqItem.answer}</p>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {slug === 'kloakmester-arbejde' && (
                <div className="space-y-12">
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-stone-900">
                      {page.introTitle === "Alt kloakarbejde udføres, ingen opgaver er for små for os" ? (
                        <>
                          Alt kloakarbejde udføres,<br />
                          ingen opgaver er for små for os
                        </>
                      ) : (
                        page.introTitle
                      )}
                    </h2>
                    <p className="text-lg text-stone-600 leading-relaxed">{renderFormattedText(page.introText)}</p>
                    <h3 className="text-xl font-bold text-stone-900 pt-4">{page.listTitle}</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {page.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 bg-stone-50 p-4 rounded-lg border border-stone-100">
                          <CheckCircle2 className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                          <span className="text-stone-700 font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-lg text-stone-600 leading-relaxed pt-4">
                      {renderFormattedText(page.outroText)}
                    </p>
                  </div>

                  {/* Interactive Sub-services Section */}
                  <div className="border-t border-stone-100 pt-12 space-y-8">
                    <h3 className="text-2xl font-bold text-stone-900">Vores specialiserede kloakydelser</h3>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(subServices).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => setSearchParams({ service: key })}
                          className={`px-5 py-3 rounded-lg font-semibold text-sm transition-all ${
                            effectiveActiveService === key
                              ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                              : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                          }`}
                        >
                          {value.title}
                        </button>
                      ))}
                    </div>

                    {subServices[effectiveActiveService] && (
                      <div className="bg-orange-50/50 border border-orange-100 p-8 rounded-2xl space-y-6 animate-fadeIn">
                        <h3 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
                          <span className="w-2 h-6 bg-orange-600 rounded-full inline-block"></span>
                          {subServices[effectiveActiveService].title}
                        </h3>
                        <p className="text-lg text-stone-700 leading-relaxed">
                          {subServices[effectiveActiveService].description}
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                          {subServices[effectiveActiveService].points.map((point, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <CheckCircle2 className="w-5 h-5 text-orange-600 shrink-0" />
                              <span className="text-stone-700 font-medium">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* FAQ Section */}
                  {page.faq && page.faq.display_on_page && page.faq.items && page.faq.items.length > 0 && (
                    <div className="pt-10 border-t border-stone-200 space-y-6">
                      <div className="space-y-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                          <i className="fa-solid fa-circle-question text-[11px]" aria-hidden="true" /> Spørgsmål & Svar
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
                          {page.faq.section_heading?.text || 'Ofte stillede spørgsmål'}
                        </h2>
                      </div>

                      <div className="space-y-4 pt-2">
                        {page.faq.items.map((faqItem, fIdx) => {
                          const isOpen = openFaqIdx === fIdx;
                          return (
                            <div 
                              key={fIdx} 
                              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                                isOpen 
                                  ? 'border-orange-200 bg-orange-50/30 shadow-sm' 
                                  : 'border-stone-200 bg-white hover:border-stone-300'
                              }`}
                            >
                              <h3 className="m-0 p-0 text-inherit font-inherit">
                                <button
                                  type="button"
                                  onClick={() => setOpenFaqIdx(isOpen ? null : fIdx)}
                                  className="w-full flex items-center justify-between p-5 text-left font-bold text-lg text-stone-900 hover:text-orange-600 transition-colors focus:outline-none cursor-pointer"
                                  aria-expanded={isOpen}
                                >
                                  <span className="pr-4">{faqItem.question}</span>
                                  <ChevronDown 
                                    className={`w-5 h-5 text-stone-500 transition-transform duration-300 shrink-0 ${
                                      isOpen ? 'transform rotate-180 text-orange-600' : ''
                                    }`} 
                                  />
                                </button>
                              </h3>

                              <AnimatePresence initial={false}>
                                {isOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: "easeInOut" }}
                                  >
                                    <div className="px-5 pb-5 text-stone-600 leading-relaxed border-t border-stone-100 pt-3">
                                      <p>{renderFormattedText(faqItem.answer)}</p>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {slug === 'ledige-stillinger' && (
                <div className="space-y-12">
                  {page.sections.map((section, idx) => (
                    <div key={idx} className="space-y-4">
                      <h2 className="text-2xl font-bold text-stone-900">{section.title}</h2>
                      {section.paragraphs.map((p, pIdx) => (
                        <p key={pIdx} className="text-lg text-stone-600 leading-relaxed">
                          {renderTextWithBold(p)}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Generic Sections Renderer for pages with sections as Accordions */}
              {!['haveservice', 'anlaeg-og-belaegning', 'stoebning-af-beton', 'traefaeldning-og-haveservice', 'kloakmester-arbejde', 'ny-indkoersel', 'ledige-stillinger', 'kloak-separering', 'kloakseparering', 'entreprenoerarbejde', 'kloakservice', 'tv-inspektion', 'inspektion'].includes(slug) && page.sections && (
                <div className="space-y-4">
                  {page.sections.map((section, idx) => {
                    const isOpen = openAccordionIdx === idx;
                    return (
                      <div 
                        key={idx} 
                        className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                          isOpen 
                            ? 'border-orange-200 bg-orange-50/20 shadow-sm' 
                            : 'border-stone-200 bg-white hover:border-stone-300'
                        }`}
                      >
                        <button
                          onClick={() => setOpenAccordionIdx(isOpen ? null : idx)}
                          className="w-full flex items-center justify-between p-6 text-left font-bold text-xl text-stone-900 hover:text-orange-600 transition-colors focus:outline-none cursor-pointer"
                        >
                          <span>{section.title}</span>
                          <ChevronDown 
                            className={`w-6 h-6 text-stone-500 transition-transform duration-300 shrink-0 ml-4 ${
                              isOpen ? 'transform rotate-180 text-orange-600' : ''
                            }`} 
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                              <div className="px-6 pb-6 space-y-4 border-t border-stone-100/50 pt-4">
                                {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                                  <p key={pIdx} className="text-lg text-stone-600 leading-relaxed">
                                    {renderTextWithBold(p)}
                                  </p>
                                ))}
                                {section.list && (
                                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                                    {section.list.map((item, i) => (
                                      <li key={i} className="flex items-start gap-3 bg-stone-50 p-4 rounded-lg border border-stone-100">
                                        <CheckCircle2 className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
                                        <span className="text-stone-700 font-medium">{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {section.outro && (
                                  <p className="text-lg text-stone-600 leading-relaxed italic font-medium pt-4">
                                    {section.outro}
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Column: Sidebar */}
            <div className="space-y-12">
              {otherServices.length > 0 && (
                <div className="bg-stone-900 text-white p-8 rounded-2xl">
                  <h3 className="text-xl font-bold mb-6">Andre ydelser</h3>
                  <div className="space-y-4">
                    {otherServices.map((s) => (
                      <Link
                        key={s.id}
                        to={`/${s.slug}`}
                        className="flex items-center justify-between group p-3 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <span className="font-medium text-stone-300 group-hover:text-white transition-colors">{s.title}</span>
                        <ChevronRight className="w-4 h-4 text-orange-500 transition-transform group-hover:translate-x-1" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="border border-stone-200 p-8 rounded-2xl">
                <h3 className="text-xl font-bold mb-6">Har du spørgsmål?</h3>
                <p className="text-stone-600 mb-8">
                  Kontakt os for en uforpligtende snak om din opgave. Vi rådgiver gerne om den bedste løsning.
                </p>
                <div className="space-y-4">
                  <a href="tel:+4522458567" className="flex items-center gap-3 font-bold text-stone-900 hover:text-orange-600 transition-colors">
                    <Icons.Phone className="w-5 h-5 text-orange-600" />
                    22 45 85 67
                  </a>
                  <a href="mailto:jan@janhedegaard.dk" className="flex items-center gap-3 font-bold text-stone-900 hover:text-orange-600 transition-colors">
                    <Icons.Mail className="w-5 h-5 text-orange-600" />
                    jan@janhedegaard.dk
                  </a>
                </div>
              </div>

              {(() => {
                const sidebarBoxConfig = {
                  'anlaeg-og-belaegning': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/d9638e5f-2807-4453-b782-d422116e01b4.jpg',
                    alt: 'Professionel belægning og anlæg',
                    title: 'Se vores belægningsarbejde',
                    description: 'Vi udfører alt inden for belægning og anlæg af indkørsler, terrasser og gangstier.',
                    category: 'Belægning og Anlæg'
                  },
                  'ny-indkoersel': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/d9638e5f-2807-4453-b782-d422116e01b4.jpg',
                    alt: 'Professionel belægning og anlæg',
                    title: 'Se vores belægningsarbejde',
                    description: 'Vi udfører alt inden for belægning og anlæg af indkørsler, terrasser og gangstier.',
                    category: 'Belægning og Anlæg'
                  },
                  'kloakmester-arbejde': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20250517-070819216-ios_1.jpg',
                    alt: 'Professionelt kloakarbejde',
                    title: 'Se vores kloakarbejde',
                    description: 'Vi er autoriseret kloakmester og udfører alt fra separering til TV-inspektion.',
                    category: 'Kloak'
                  },
                  'kloakseparering': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20260329-070521889-ios-1.jpg',
                    alt: 'Professionelt kloakarbejde',
                    title: 'Se vores kloakarbejde',
                    description: 'Vi er autoriseret kloakmester og udfører alt fra separering til TV-inspektion.',
                    category: 'Kloak'
                  },
                  'kloak-separering': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20260329-070521889-ios-1.jpg',
                    alt: 'Professionelt kloakarbejde',
                    title: 'Se vores kloakarbejde',
                    description: 'Vi er autoriseret kloakmester og udfører alt fra separering til TV-inspektion.',
                    category: 'Kloak'
                  },
                  'kloakservice': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20250517-070815450-ios.jpg',
                    alt: 'Professionelt kloakservice',
                    title: 'Se vores kloakarbejde',
                    description: 'Vi er autoriseret kloakmester og udfører alt fra kloakrensning og spuling til reparation og renovering.',
                    category: 'Kloak'
                  },
                  'tv-inspektion': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20250517-070819216-ios_1.jpg',
                    alt: 'Professionelt kloakarbejde',
                    title: 'Se vores kloakarbejde',
                    description: 'Vi er autoriseret kloakmester og udfører alt fra separering til TV-inspektion.',
                    category: 'Kloak'
                  },
                  'haveservice': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20250504-075257394-ios-rotated.jpg',
                    alt: 'Professionel haveservice',
                    title: 'Se vores haveservice',
                    description: 'Vi holder dine grønne områder smukke og velplejede året rundt.',
                    category: 'Haveservice'
                  },
                  'stoebning-af-beton': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/beton.jpg',
                    alt: 'Professionelt betonarbejde',
                    title: 'Se vores betonarbejde',
                    description: 'Vi udfører professionel støbning af fundamenter, gulve og trapper.',
                    category: 'Beton og Fundament'
                  },
                  'traefaeldning-og-haveservice': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/beaaee35-3499-4378-81f6-c05b5114034e.jpg',
                    alt: 'Professionel træfældning',
                    title: 'Se vores træfældning',
                    description: 'Sikker og professionel fældning samt beskæring af alle typer træer.',
                    category: 'Træfældning og beskæring'
                  },
                  'entreprenoerarbejde': {
                    imgSrc: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/entr-arbejde1.jpg',
                    alt: 'Udgravning og terrænregulering med minigraver og entreprenørmateriel',
                    title: 'Se vores entreprenørarbejde',
                    description: 'Vi udfører professionelt jord-, grave- og anlægsarbejde til ethvert projekt.',
                    category: 'Entreprenørarbejde'
                  }
                };

                const config = sidebarBoxConfig[slug] || sidebarBoxConfig['anlaeg-og-belaegning'];
                const optimizedImg = config.imgSrc.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/');

                return (
                  <div className="overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
                    <div className="aspect-[4/3] overflow-hidden relative group">
                      <img 
                        src={optimizedImg} 
                        alt={config.alt || `${config.title} hos Entreprenør Jan Hedegaard`} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
                    </div>
                    <div className="p-6">
                      <p className="font-bold text-stone-900 mb-2 text-base">{config.title}</p>
                      <p className="text-sm text-stone-600 mb-4">
                        {config.description}
                      </p>
                      <Link 
                        to={`/galleri?category=${encodeURIComponent(config.category)}`}
                        className="inline-flex w-full items-center justify-center gap-2 bg-orange-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-orange-700 transition-all shadow-md hover:shadow-lg hover:shadow-orange-600/20"
                      >
                        Se galleri for {config.category}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      {!['anlaeg-og-belaegning', 'stoebning-af-beton', 'traefaeldning-og-haveservice'].includes(slug) && (
        <section className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ContactBox 
              title={slug === 'kloakservice' ? 'Få et tilbud på kloakservice' : (slug === 'kloakseparering' || slug === 'kloak-separering') ? 'Få et tilbud på kloakseparering' : slug === 'tv-inspektion' ? 'Få et tilbud på TV-inspektion' : `Få et tilbud på ${page.headline.toLowerCase()}`}
              subtitle="Vi står klar til at hjælpe dig med din næste opgave. Kontakt os i dag for et uforpligtende tilbud."
            />
          </div>
        </section>
      )}
    </>
  );
}
